import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { X, Info, AlertTriangle, CheckCircle, AlertCircle, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"

interface BroadcastMessage {
  id: number
  text: string
  type: "info" | "warning" | "success" | "error"
  is_dismissible: boolean
  link_url?: string | null
  link_text?: string | null
}

const typeConfig = {
  info: {
    bg: "bg-blue-500",
    text: "text-white",
    icon: Info,
  },
  warning: {
    bg: "bg-yellow-500",
    text: "text-yellow-950",
    icon: AlertTriangle,
  },
  success: {
    bg: "bg-green-500",
    text: "text-white",
    icon: CheckCircle,
  },
  error: {
    bg: "bg-red-500",
    text: "text-white",
    icon: AlertCircle,
  },
}

export function BroadcastBanner() {
  const [dismissed, setDismissed] = useState<number | null>(null)

  // Check localStorage for previously dismissed banners
  useEffect(() => {
    const dismissedId = localStorage.getItem("dismissed_broadcast_id")
    if (dismissedId) {
      setDismissed(parseInt(dismissedId, 10))
    }
  }, [])

  const { data } = useQuery({
    queryKey: ["broadcast-message"],
    queryFn: async () => {
      // VITE_API_URL already includes /api, so just append /broadcast
      const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api"
      const response = await fetch(`${baseUrl}/broadcast`)
      if (!response.ok) return null
      const data = await response.json()
      return data.message as BroadcastMessage | null
    },
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 300000, // Refetch every 5 minutes
    refetchIntervalInBackground: false,
  })

  const handleDismiss = () => {
    if (data?.id) {
      localStorage.setItem("dismissed_broadcast_id", data.id.toString())
      setDismissed(data.id)
    }
  }

  // Don't show if no message, or if this message was dismissed
  if (!data || dismissed === data.id) {
    return null
  }

  const config = typeConfig[data.type] || typeConfig.info
  const Icon = config.icon

  return (
    <div className={cn("flex items-center justify-center gap-2 px-4 py-2 text-sm", config.bg, config.text)}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 text-center">
        {data.text}
        {data.link_url && (
          <a
            href={data.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
          >
            {data.link_text || "Learn more"}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </span>
      {data.is_dismissible && (
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
